/**
 * Per-customer thresholds (SW-8879): how many of a customer's tractors must be
 * production ready. Tractor Metrics turns the customer's card red below it.
 * One number per customer, saved on its own; blank clears it. Lives on the
 * admin (schema editor) page: the route it writes is fleet-admin only.
 */
import React, { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";
import { Button } from "../../components/ui/button";
import {
  fetchCustomerSettings,
  patchCustomerSetting,
} from "../TractorMetrics/api";
import { CustomerSetting } from "../TractorMetrics/types";
import { Card, Eyebrow } from "./styled";
import { tokens } from "./tokens";

interface CustomerThresholdsProps {
  token: string;
}

/** The tenant vocabulary's labels; anything new reads as its own name. */
const LABELS: Record<string, string> = { aerovect: "AeroVect", delta: "Delta" };

const labelOf = (customer: string) =>
  LABELS[customer] ?? customer.charAt(0).toUpperCase() + customer.slice(1);

function describeSaved(row: CustomerSetting): string | null {
  if (!row.updated_at) return null;
  const when = new Date(row.updated_at).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return row.updated_by
    ? `set by ${row.updated_by} on ${when}`
    : `set on ${when}`;
}

const CustomerThresholds: React.FC<CustomerThresholdsProps> = ({ token }) => {
  const [rows, setRows] = useState<CustomerSetting[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<
    Record<string, { tone: "ok" | "bad"; text: string }>
  >({});

  const load = useCallback(() => {
    setLoadError(null);
    fetchCustomerSettings(token).then(
      (res) => {
        setRows(res.customers);
        setDrafts(
          Object.fromEntries(
            res.customers.map((c) => [
              c.customer,
              c.min_production_ready === null
                ? ""
                : String(c.min_production_ready),
            ]),
          ),
        );
      },
      () => setLoadError("The thresholds did not load."),
    );
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (customer: string) => {
    const raw = (drafts[customer] ?? "").trim();
    const value = raw === "" ? null : Number(raw);
    if (value !== null && (!Number.isInteger(value) || value < 0)) {
      setStatus((s) => ({
        ...s,
        [customer]: { tone: "bad", text: "Enter a whole number, 0 or more." },
      }));
      return;
    }
    setSaving((s) => ({ ...s, [customer]: true }));
    try {
      const saved = await patchCustomerSetting(token, customer, value);
      setRows((prev) =>
        (prev ?? []).map((r) => (r.customer === customer ? saved : r)),
      );
      setStatus((s) => ({ ...s, [customer]: { tone: "ok", text: "Saved." } }));
    } catch (err) {
      setStatus((s) => ({
        ...s,
        [customer]: {
          tone: "bad",
          text:
            err instanceof Error ? err.message : "The threshold was not saved.",
        },
      }));
    } finally {
      setSaving((s) => ({ ...s, [customer]: false }));
    }
  };

  return (
    <ThresholdCard>
      <Eyebrow as="h2">Customer thresholds</Eyebrow>
      <Help>
        How many of each customer&apos;s tractors must be production ready.
        Tractor Metrics turns the customer&apos;s card red below this number.
        Blank means no threshold.
      </Help>
      {loadError && (
        <Notice role="alert">
          {loadError}{" "}
          <Button type="button" variant="ghost" size="sm" onClick={load}>
            Retry
          </Button>
        </Notice>
      )}
      {rows && (
        <Rows>
          {rows.map((row) => {
            const label = labelOf(row.customer);
            const note = status[row.customer];
            const saved = describeSaved(row);
            return (
              <Row key={row.customer}>
                <Label htmlFor={`threshold-${row.customer}`}>{label}</Label>
                <NumberInput
                  id={`threshold-${row.customer}`}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={drafts[row.customer] ?? ""}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [row.customer]: e.target.value }))
                  }
                />
                <Button
                  type="button"
                  size="sm"
                  aria-label={`Save ${label}`}
                  disabled={Boolean(saving[row.customer])}
                  onClick={() => save(row.customer)}
                >
                  {saving[row.customer] ? "Saving…" : "Save"}
                </Button>
                <Status $tone={note?.tone ?? "ok"}>
                  {note?.text ??
                    saved ??
                    (row.min_production_ready === null ? "not set" : "")}
                </Status>
              </Row>
            );
          })}
        </Rows>
      )}
    </ThresholdCard>
  );
};

export default CustomerThresholds;

const ThresholdCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 18px;
  margin-bottom: 16px;
`;

const Help = styled.p`
  margin: 0;
  font: 12.5px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 110px 100px auto minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  @media (max-width: 600px) {
    grid-template-columns: 1fr 100px auto;
    & > :last-child {
      grid-column: 1 / -1;
    }
  }
`;

const Label = styled.label`
  font: 600 13px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const NumberInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.ink};
  font: 14px ${tokens.fontSans};
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const Status = styled.span<{ $tone: "ok" | "bad" }>`
  font: 12px ${tokens.fontSans};
  color: ${(p) => (p.$tone === "bad" ? tokens.groundedText : tokens.slate)};
  min-width: 0;
`;

const Notice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font: 13px ${tokens.fontSans};
  color: ${tokens.ink};
`;
